import { authFetch } from "./auth/authFetch";
import { supabase } from "./auth/supabase";
import { getCloudBoardId } from "./data/cloudBoard";

// Consumes one export slot BEFORE any encode work (fail fast — don't render
// 300 frames only to refuse the download). Accepted trade-off: a failed
// encode after consume burns a slot; rare enough to skip a refund endpoint.

const API_BASE = import.meta.env.VITE_APP_API_URL ?? "http://localhost:8787";

export async function requestExport(format: string): Promise<void> {
  // Signed-out means local-only work: there is no account to meter, and the
  // consume RPC would only 401 (bouncing the user to login mid-export). The
  // weekly cap is enforced server-side for signed-in accounts; anonymous
  // export stays free like upstream Excalidraw.
  const { data } = await supabase.auth.getSession();
  if (!data.session) {
    return;
  }
  let res: Response;
  try {
    res = await authFetch(`${API_BASE}/api/exports/consume`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ format, boardId: getCloudBoardId() }),
    });
  } catch {
    throw new Error(
      "Export needs a connection to check your plan. Retry when you're back online.",
    );
  }
  if (res.status === 402) {
    const body = (await res.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new Error(
      body?.message ??
        "You've used all your free exports this week. Upgrade to Creator for unlimited exports.",
    );
  }
  // A 5xx is OUR failure, not the user's — the plan check is unavailable, which
  // is not the same as the answer being "no". Failing closed here turns any API
  // outage (or a missing consume_export migration) into "nobody can export
  // anything", which is far worse than letting a few exports through unmetered.
  // The cap's real enforcement is the atomic RPC, and this path is only
  // reachable when that RPC is already broken.
  if (res.status >= 500) {
    console.warn(
      `Export slot check failed (${res.status}); allowing the export.`,
    );
    return;
  }
  if (!res.ok) {
    throw new Error("Export couldn't start. Please try again.");
  }
}
